/**
 * RUM Service - App Launch Event Only
 *
 * Simplified RUM service focused on app launch event for testing RUM pipeline
 */

import { AwsRum } from "aws-rum-web";
import { BusinessEventPayload, buildBusinessEventPayload } from "./rumEventHelpers";
import { fetchCredentials } from "@utils/appsync";

// RUM App Configuration Interface
export interface RumAppConfig {
    applicationId: string;
    applicationVersion: string;
    applicationRegion: string;
    identityPoolId: string;
    crossAccountRoleArn?: string;
    config: {
        sessionSampleRate?: number;
        telemetries?: Array<string>;
        allowCookies?: boolean;
        enableXRay?: boolean;
        signing?: boolean;
        sessionEventLimit?: number;
        endpoint?: string;
    };
}

class RumService {
    private awsRum: any = null;
    private isInitialized = false;

    async initialize(rumAppConfig: RumAppConfig): Promise<void> {
        if (this.isInitialized) return;

        try {
            console.log("RUM Configuration:", {
                applicationId: rumAppConfig.applicationId,
                applicationVersion: rumAppConfig.applicationVersion,
                applicationRegion: rumAppConfig.applicationRegion,
            });

            // Initialize RUM with basic configuration
            const rumConfig: any = {
                sessionSampleRate: rumAppConfig.config.sessionSampleRate || 1.0,
                telemetries: rumAppConfig.config.telemetries || ["performance", "errors", "http"],
                allowCookies: rumAppConfig.config.allowCookies !== undefined ? rumAppConfig.config.allowCookies : true,
                enableXRay: rumAppConfig.config.enableXRay !== undefined ? rumAppConfig.config.enableXRay : true,
                signing: rumAppConfig.config.signing !== undefined ? rumAppConfig.config.signing : true,
                sessionEventLimit: rumAppConfig.config.sessionEventLimit || 2000,
                endpoint: rumAppConfig.config.endpoint,
                //identityPoolId: rumAppConfig.identityPoolId,
            };

            console.log("Final RUM config:", rumConfig);

            this.awsRum = new AwsRum(
                rumAppConfig.applicationId,
                rumAppConfig.applicationVersion,
                rumAppConfig.applicationRegion,
                rumConfig,
            );

            // Only setup credentials when signing in the browser (localhost dev)
            // When signing is false (deployed with /rum-data proxy), Lambda@Edge handles signing
            if (rumConfig.signing) {
                await this.setupCrossAccountCredentials();
            }

            this.isInitialized = true;
            console.log("RUM initialized successfully");
        } catch (error) {
            console.error("RUM initialization failed:", error);
            throw error;
        }
    }

    /**
     * Setup cross-account credentials for RUM access (optional)
     */
    private async setupCrossAccountCredentials(): Promise<void> {
        try {
            const credentials = await fetchCredentials();

            this.awsRum.setAwsCredentials(credentials);

            console.log("Cross-account credentials set up successfully");
        } catch (error) {
            console.error("Failed to setup cross-account credentials:", error);
            console.warn("Falling back to Cognito Identity Pool authentication");
        }
    }

    /**
     * Record a Metrics One formatted RUM event.
     * Uses the standardized payload structure for OpenSearch ingestion.
     *
     * @param eventType - RUM event type (e.g. "a3s-business-event") — must contain "*-business" for Lambda filter
     * @param action - User action (e.g. "app_launch", "button_click")
     * @param actionIdentifier - Action context (e.g. "APPLICATION_LAUNCHED")
     * @param userAlias - User login alias
     * @param metadata - Optional open JSON for additional metrics data
     */
    async recordBusinessEvent(
        eventType: string,
        action: string,
        actionIdentifier: string,
        userAlias: string,
        metadata?: Record<string, any>,
    ): Promise<void> {
        if (this.isInitialized && this.awsRum) {
            try {
                const payload: BusinessEventPayload = buildBusinessEventPayload(action, actionIdentifier, userAlias, metadata);
                await this.awsRum.recordEvent(eventType, payload);
                console.log(`RUM Metrics One event recorded: ${eventType}`);
            } catch (error) {
                console.error(`Error recording RUM Metrics One event '${eventType}':`, error);
            }
        } else {
            console.warn(`RUM not initialized, cannot record Metrics One event: ${eventType}`);
        }
    }

    /**
     * Get current RUM session ID
     */
    getSessionId(): string {
        return this.awsRum?.getSessionId?.() || "unknown";
    }

    /**
     * Check if RUM is initialized
     */
    isRumInitialized(): boolean {
        return this.isInitialized;
    }
}

// Create singleton instance
export const rumService = new RumService();
